/**
 * The `MFA_VERIFICATION` step: a code from an authenticator that is already enrolled.
 *
 * The input accepts EITHER a six-digit TOTP code OR a recovery code, because the endpoint
 * does: `/auth/login/mfa` tries the TOTP secret first and, only when the input is not six
 * digits, falls back to bcrypt-comparing it against the stored recovery-code hashes. The
 * value is sent trimmed but otherwise untouched — `zMfaCode` strips spaces and hyphens
 * server-side, so mangling `AB12-CD34-EF56` here would only risk breaking a format the
 * server already understands.
 *
 * The step token is NOT discarded on a rejected code. Verified live: the same token was
 * accepted immediately after a wrong code was refused. Clearing it would force a user who
 * fat-fingered one digit to re-enter their password, which is precisely the pointless loop
 * this screen exists to remove.
 */
import { useState, type FormEvent } from 'react';
import { ShieldCheck } from 'lucide-react';

import { api } from '@frontend/api/client';
import { Button } from '@frontend/components/ui/Button';
import { FieldShell, Input } from '@frontend/components/ui/Field';
import { Banner } from '@frontend/components/ui/Page';

import { describeAuthFailure } from './authErrors';
import { readLoginOutcome, unrecognisedOutcome, type LoginOutcome } from './loginSteps';

interface Props {
  stepToken: string;
  message: string;
  onAdvance: (outcome: LoginOutcome) => void;
  onRestart: (reason: string) => void;
}

export function MfaVerification({ stepToken, message, onAdvance, onRestart }: Props): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setCodeError(null);
    setBusy(true);
    const form = new FormData(event.currentTarget);
    try {
      const body = await api.post<unknown>('/auth/login/mfa', {
        stepToken,
        code: String(form.get('code') ?? '').trim(),
      });
      const outcome = readLoginOutcome(body);
      if (outcome === null) {
        setError(unrecognisedOutcome('POST /api/auth/login/mfa'));
        return;
      }
      onAdvance(outcome);
    } catch (caught) {
      const failure = describeAuthFailure(
        caught,
        'MFA_VERIFICATION',
        'The code could not be checked and no reason was returned. Check your connection and try again.',
      );
      if (failure.restart) {
        onRestart(failure.message);
        return;
      }
      setError(failure.message);
      setCodeError(failure.fieldErrors['code'] ?? null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)} data-testid="mfa-form">
      <h1 className="mb-1 text-section font-semibold text-content">Two-factor code</h1>
      <p className="mb-4 text-row text-content-secondary" data-testid="step-message">
        {message}
      </p>

      {error !== null && (
        <Banner tone="error" className="mb-4" data-testid="login-error">
          {error}
        </Banner>
      )}

      <FieldShell
        label="Authentication code"
        htmlFor="mfa-code"
        required
        fieldKey="code"
        error={codeError}
        hint="Six digits from your authenticator app, or one of your recovery codes."
      >
        <Input
          id="mfa-code"
          name="code"
          rawName
          autoComplete="one-time-code"
          inputMode="text"
          maxLength={20}
          required
          autoFocus
          invalid={codeError !== null}
          placeholder="000000"
          className="tabular tracking-[0.24em]"
          data-testid="input-mfa-code"
        />
      </FieldShell>

      <Button
        type="submit"
        variant="primary"
        fullWidth
        className="mt-4"
        isLoading={busy}
        leadingIcon={<ShieldCheck aria-hidden className="h-3.5 w-3.5" />}
        data-testid="submit-mfa"
      >
        Verify
      </Button>
    </form>
  );
}
